import cn from 'classnames';
import styles from './pagePicker.scss';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { Select, Option } from 'components/Select';
import { Button, LoaderButton } from 'components/Button';
import { rm, range } from 'utils';

interface PagePickerProps {
  className?: string;
  pageNbr: number;
  nbrOfPages: number;
  rootCategoryUrl: string;
  nextPage: () => Promise<unknown>;
  prevPage: () => Promise<unknown>;
  gotoPage: (page: number) => void;
  showLoading: boolean;
}

export const PagePicker = (props: PagePickerProps) => (
  <>
    <MobilePagePicker {...props} />
    <DesktopPagePicker {...props} />
  </>
);

const MobilePagePicker = ({ className, pageNbr, nbrOfPages, gotoPage }: PagePickerProps) => {
  const decoratedGotoPage = (e: React.ChangeEvent<HTMLSelectElement>) =>
    void gotoPage(Number(e.target.value));

  return (
    <div className={cn(className, styles.pagePicker, styles.mobilePagePicker)}>
      <select
        className={cn(styles.select, styles.label)}
        onChange={decoratedGotoPage}
        value={pageNbr}
      >
        {range(1, nbrOfPages).map(page => (
          <option key={page} value={page}>
            {rm('productlist_page_x_of_y', page, nbrOfPages)}
          </option>
        ))}
      </select>
      <div className={styles.labelContainer}>
        <div className={styles.labelWrapper}>
          <span className={styles.label}>{rm('productlist_page_x_of_y', pageNbr, nbrOfPages)}</span>
        </div>
        <IconArrow className={styles.icon} size="small" appearance="flip-90" />
      </div>
    </div>
  );
};

const getPrevPageLink = ({
  pageNbr,
  rootCategoryUrl
}: Pick<PagePickerProps, 'pageNbr' | 'rootCategoryUrl'>) => {
  const previousPage = pageNbr - 1;
  const hasPreviousPage = pageNbr !== 1;
  const isPreviousPageFirst = previousPage === 1;

  if (isPreviousPageFirst || !hasPreviousPage) {
    return undefined;
  }

  return `${rootCategoryUrl}?page=${previousPage}`;
};

const DesktopPagePicker = ({
  className,
  pageNbr,
  nbrOfPages,
  rootCategoryUrl,
  nextPage,
  prevPage,
  gotoPage,
  showLoading
}: PagePickerProps) => {
  const decoratedNextPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.blur();
    return nextPage();
  };
  const decoratedPrevPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.blur();
    return prevPage();
  };
  const decoratedGotoPage = (page: string) => void gotoPage(Number(page));

  const hasPrevPage = pageNbr !== 1;
  const hasNextPage = pageNbr !== nbrOfPages;

  const prevPageLink = getPrevPageLink({ pageNbr, rootCategoryUrl });
  const nextPageLink = hasNextPage ? `${rootCategoryUrl}?page=${pageNbr + 1}` : undefined;

  const ButtonTypeToShow = showLoading ? LoaderButton : Button;

  return (
    <div className={cn(className, styles.pagePicker, styles.desktopPagePicker)}>
      <ButtonTypeToShow
        href={prevPageLink}
        appearance="tertiary"
        onClick={decoratedPrevPage}
        disabled={!hasPrevPage}
        className={cn(styles.navButton, styles.prev)}
      >
        <IconArrow thickness="thin" appearance="flip-180" />
      </ButtonTypeToShow>
      <div className={styles.page}>
        <Select
          onChange={decoratedGotoPage}
          scrollable
          selectedValue={pageNbr}
          className={styles.desktopSelect}
          classNames={{
            input: styles.selectInput,
            selected: styles.selected
          }}
        >
          {range(1, nbrOfPages).map(pageNbr => (
            <Option
              key={pageNbr}
              value={pageNbr}
              label={rm('productlist_page_x_of_y', pageNbr, nbrOfPages)}
            />
          ))}
        </Select>
      </div>
      <ButtonTypeToShow
        href={nextPageLink}
        onClick={decoratedNextPage}
        appearance="tertiary"
        disabled={!hasNextPage}
        className={cn(styles.navButton, styles.next)}
      >
        <IconArrow thickness="thin" />
      </ButtonTypeToShow>
    </div>
  );
};
